import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { Button } from "@mui/material";
import TextField from "../../../components/SanitizedTextField/SanitizedTextField";
import {
  formatDisplayValue,
  isNetMeterDocumentList,
  meterLabelFromDocument,
  sanitizeReadingInput,
  toNumberOrNull,
} from "../../BillingAndCollection/MeterReadingCorrection/mrcReadingUtils";
import MrcEvidencePreview from "../../BillingAndCollection/MeterReadingCorrection/MrcEvidencePreview";
import OpenBase64ImageInPopup from "../../../services/OpenBase64ImageInPopup";
import { handleFileUpload } from "../../../services/Functions";
import { documentExtensions } from "../../../utils/PortalName";
import "../../BillingAndCollection/MeterReadingCorrection/MeterReadingCorrectionForm.scss";
import "./DcoRemovedMeterReadingTable.scss";

const DIAL_LABEL = {
  1: "KWH",
  2: "KVARH",
  3: "MDI",
};

const normalizeDialType = (item = {}) => {
  const raw = String(
    item.meterDialType || item.meterType || item.meterLabel || item.METER_TYPE || "",
  ).trim();
  if (/kvarh/i.test(raw)) return "KVARH";
  if (/mdi/i.test(raw)) return "MDI";
  if (/kwh/i.test(raw)) return "KWH";
  const meterCode = String(item.METER_CODE || item.meterCode || "");
  if (meterCode.includes("06")) return "KVARH";
  if (meterCode.includes("08")) return "MDI";
  return DIAL_LABEL[Number(item.meterId ?? item.METER_ID)] || raw || "KWH";
};

const peakOffPeakBand = (item, { isTou = false } = {}) => {
  const code = String(item.meterCode ?? item.METER_CODE ?? "").trim().toLowerCase();
  if (code === "050600" || code.endsWith("0600") || code.endsWith("0506") || (code.includes("peak") && !code.includes("off"))) {
    return "Peak";
  }
  if (code === "050500" || code.endsWith("0500") || code.endsWith("0505") || code.includes("off")) {
    return "Off Peak";
  }
  const setNo = Number(item.setNo ?? item.SET_NO ?? 1);
  if (isTou && Number.isFinite(setNo) && setNo > 0) {
    return setNo % 2 === 1 ? "Off Peak" : "Peak";
  }
  return "";
};

export const mapDcoRemovedMeterUnits = (units = [], options = {}) => {
  const list = Array.isArray(units) ? units : [];
  const tariff = String(options.tariffCode ?? options.tariffCd ?? "").trim();
  const isNetMeter = tariff === "12" || isNetMeterDocumentList(list);
  const kwhSetCount = new Set(
    list
      .filter((item) => normalizeDialType(item) === "KWH")
      .map((item) => Number(item.setNo ?? item.SET_NO ?? 1))
      .filter((n) => Number.isFinite(n) && n > 0),
  ).size;
  const hasTouCode = list.some((item) => Boolean(peakOffPeakBand(item)));
  const isTou = isNetMeter || kwhSetCount >= 2 || hasTouCode;

  return list.map((item, index) => {
    const setNo = item.setNo ?? item.SET_NO ?? 1;
    const doc = {
      ...item,
      meterId: item.meterId ?? item.METER_ID,
      METER_ID: item.METER_ID ?? item.meterId,
      meterCode: item.meterCode ?? item.METER_CODE,
      METER_CODE: item.METER_CODE ?? item.meterCode,
      setNo,
      SET_NO: item.SET_NO ?? setNo,
    };
    let meterDialType = meterLabelFromDocument(doc, {
      isNetMeter,
      isTou,
      hasTouKwh: isTou,
      kwhSetCount,
    });
    if (!meterDialType || meterDialType === "—") {
      const dial = normalizeDialType(item);
      const band = peakOffPeakBand(item, { isTou });
      meterDialType = band ? `${dial} ${band}` : dial;
    }
    const isMdi =
      Number(item.meterId ?? item.METER_ID) === 3
      || /mdi/i.test(String(meterDialType))
      || normalizeDialType(item) === "MDI";
    return {
      id: String(item.id ?? item.Id ?? `${setNo}-${item.meterId ?? item.METER_ID ?? index}`),
      setNo,
      isMdi,
      meterDialType,
      serialNo: item.serialNo || item.meterSerialNo || item.SERIAL_NO || item.METER_SERIAL_NO || "",
      feederNo: item.feederNo || item.feederNumber || item.FEEDER_NO || item.FEEDER_CODE || "",
      mf: item.mf ?? item.MF ?? item.multiplyingFactor ?? item.MULTIPLY_FACTOR ?? "",
      masterReading:
        item.masterReading
        ?? item.MASTER_READING
        ?? item.previousReading
        ?? item.METER_READING
        ?? "",
      removedMeterReading: item.removedMeterReading ?? item.meterReading ?? "",
      evidence: item.evidence || item.evidenceImage || item.imageSrc || "",
    };
  });
};

const calcUnitsConsumed = (removedReading, masterReading, mf) => {
  const removed = toNumberOrNull(removedReading);
  const master = toNumberOrNull(masterReading);
  if (removed === null || master === null) return null;
  const factor = toNumberOrNull(mf);
  const multiplier = factor === null || factor === 0 ? 1 : factor;
  return Number(((removed - master) * multiplier).toFixed(2));
};

const readingErrorForRow = (value, masterReading) => {
  if (String(value ?? "").trim() === "") return "Removed Meter Reading is required.";
  const n = toNumberOrNull(value);
  if (n === null) return "Must be numeric.";
  const master = toNumberOrNull(masterReading);
  if (master != null && n < master) return "Must be ≥ Master Reading.";
  return "";
};

const DcoRemovedMeterReadingTable = forwardRef(({ units = [], readOnly = false, onCompleteChange, tariffCode = "" }, ref) => {
  const rows = useMemo(() => mapDcoRemovedMeterUnits(units, { tariffCode }), [units, tariffCode]);
  const [readings, setReadings] = useState({});
  const [readingErrors, setReadingErrors] = useState({});
  const [evidenceById, setEvidenceById] = useState({});
  const [evidenceErrors, setEvidenceErrors] = useState({});
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    const nextReadings = {};
    const nextEvidence = {};
    rows.forEach((row) => {
      nextReadings[row.id] = row.removedMeterReading != null && row.removedMeterReading !== ""
        ? String(row.removedMeterReading)
        : "";
      if (row.evidence) {
        nextEvidence[row.id] = { file: row.evidence, thumbnail: row.evidence };
      }
    });
    setReadings(nextReadings);
    setEvidenceById(nextEvidence);
    setReadingErrors({});
    setEvidenceErrors({});
    setPreview(null);
  }, [rows]);

  const isFormComplete = useMemo(() => {
    if (readOnly || !rows.length) return false;
    return rows.every((row) => {
      if (row.isMdi) return true;
      const readingErr = readingErrorForRow(readings[row.id] ?? "", row.masterReading);
      const evidence = evidenceById[row.id];
      const evidenceSrc = evidence?.thumbnail || evidence?.file || "";
      return !readingErr && Boolean(String(evidenceSrc).trim());
    });
  }, [readOnly, rows, readings, evidenceById]);

  useEffect(() => {
    onCompleteChange?.(isFormComplete);
  }, [isFormComplete, onCompleteChange]);

  const handleReadingChange = (row, value) => {
    const nextValue = sanitizeReadingInput(value);
    setReadings((prev) => ({ ...prev, [row.id]: nextValue }));
    setReadingErrors((prev) => ({
      ...prev,
      [row.id]: readingErrorForRow(nextValue, row.masterReading),
    }));
  };

  const handleEvidenceUpload = (row, event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;
    const newFileName = `dco-removed-meter-${row.id}.${file.name.split(".").pop()}`;
    handleFileUpload(
      event,
      () => { },
      (val) => {
        setEvidenceById((prev) => ({
          ...prev,
          [row.id]: { ...(prev[row.id] || {}), file: val, thumbnail: prev[row.id]?.thumbnail || "" },
        }));
      },
      (err) => {
        setEvidenceErrors((prev) => {
          const next = { ...prev };
          if (err) next[row.id] = err;
          else delete next[row.id];
          return next;
        });
      },
      (thumb) => {
        setEvidenceById((prev) => ({
          ...prev,
          [row.id]: { file: prev[row.id]?.file || "", thumbnail: thumb || "" },
        }));
        setEvidenceErrors((prev) => {
          const next = { ...prev };
          delete next[row.id];
          return next;
        });
      },
      newFileName,
    );
  };

  const clearEvidence = (rowId) => {
    setEvidenceById((prev) => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
    setEvidenceErrors((prev) => ({
      ...prev,
      [rowId]: "Evidence is required.",
    }));
  };

  const buildSubmitRows = (readingMap = readings, evidenceMap = evidenceById) =>
    rows.map((row) => {
      const readingValue = readingMap[row.id] ?? "";
      const evidence = evidenceMap[row.id];
      return {
        id: row.id,
        setNo: row.setNo,
        isMdi: Boolean(row.isMdi),
        meterDialType: row.meterDialType,
        serialNo: row.serialNo,
        feederNo: row.feederNo,
        mf: row.mf,
        masterReading: row.masterReading,
        removedMeterReading: row.isMdi ? "" : readingValue,
        unitsConsumed: row.isMdi ? null : calcUnitsConsumed(readingValue, row.masterReading, row.mf),
        evidence: row.isMdi ? "" : (evidence?.thumbnail || evidence?.file || ""),
      };
    });

  const validate = () => {
    if (readOnly) {
      return { ok: false, message: "Meter readings cannot be edited.", rows: [] };
    }
    if (!rows.length) {
      return { ok: false, message: "Meter units not loaded.", rows: [] };
    }
    const nextReadingErrors = {};
    const nextEvidenceErrors = {};
    let ok = true;
    rows.forEach((row) => {
      if (row.isMdi) return;
      const readingValue = readings[row.id] ?? "";
      const readingErr = readingErrorForRow(readingValue, row.masterReading);
      if (readingErr) {
        nextReadingErrors[row.id] = readingErr;
        ok = false;
      }
      const evidence = evidenceById[row.id];
      const evidenceSrc = evidence?.thumbnail || evidence?.file || "";
      if (!String(evidenceSrc).trim()) {
        nextEvidenceErrors[row.id] = "Evidence is required.";
        ok = false;
      }
    });
    setReadingErrors(nextReadingErrors);
    setEvidenceErrors(nextEvidenceErrors);
    return {
      ok,
      message: ok ? "" : "Enter Removed Meter Reading and upload Evidence for every KWH/KVARH meter.",
      rows: ok ? buildSubmitRows() : [],
    };
  };

  useImperativeHandle(ref, () => ({
    validate,
    getSubmitRows: () => buildSubmitRows(),
  }));

  const colgroup = (
    <colgroup>
      <col className="col-set" />
      <col className="col-meter" />
      <col className="col-serial" />
      <col className="col-feeder" />
      <col className="col-mf" />
      <col className="col-num" />
      <col className="col-removed" />
      <col className="col-num" />
      <col className="col-image" />
    </colgroup>
  );

  return (
    <div className="mrc-documents-reading-table mrc-documents-reading-table--full dco-removed-meter-table">
      <div className="mrc-meter-table">
        <div className="mrc-meter-table__head">
          <table>
            {colgroup}
            <thead>
              <tr>
                <th className="th-multiline">Set<br />No</th>
                <th className="th-multiline">Meter Dial Type</th>
                <th className="th-multiline">Serial<br />No</th>
                <th className="th-multiline">Feeder<br />No</th>
                <th>MF</th>
                <th className="th-multiline">Master<br />Reading</th>
                <th className="th-multiline">Removed Meter<br />Reading *</th>
                <th className="th-multiline">Units<br />Consumed</th>
                <th>Evidence *</th>
              </tr>
            </thead>
          </table>
        </div>
        <div className="mrc-meter-table__body">
          <table>
            {colgroup}
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={9}>
                    <p className="mrc-meter-hint">
                      Meter units will load here. All dial types (KWH, KVARH, MDI) with Peak / Off Peak will appear as rows.
                    </p>
                  </td>
                </tr>
              ) : (
                rows.map((row, index) => {
                  const readingValue = readings[row.id] ?? "";
                  const readingError = readingErrors[row.id] || "";
                  const evidence = evidenceById[row.id];
                  const evidenceSrc = evidence?.thumbnail || "";
                  const evidenceError = evidenceErrors[row.id] || "";
                  const unitsConsumed = calcUnitsConsumed(
                    readingValue,
                    row.masterReading,
                    row.mf,
                  );
                  const isNewSet = index === 0 || rows[index - 1].setNo !== row.setNo;
                  const isMdi = Boolean(row.isMdi);

                  return (
                    <tr
                      key={row.id}
                      className={isNewSet ? "row-set-start" : ""}
                    >
                      <td className="cell-text cell-set">
                        <span className={`set-badge${isNewSet ? "" : " set-badge--muted"}`}>
                          {formatDisplayValue(row.setNo)}
                        </span>
                      </td>
                      <td className="cell-text cell-meter">
                        <span className="meter-type-pill">{row.meterDialType}</span>
                      </td>
                      <td className="cell-text cell-serial">
                        {formatDisplayValue(row.serialNo)}
                      </td>
                      <td className="cell-text cell-serial">
                        {formatDisplayValue(row.feederNo)}
                      </td>
                      <td className="cell-text cell-mf">
                        {formatDisplayValue(row.mf)}
                      </td>
                      <td className="cell-text cell-num">
                        {formatDisplayValue(row.masterReading)}
                      </td>
                      <td className="cell-site-survey">
                        {readOnly || isMdi ? (
                          isMdi ? "—" : formatDisplayValue(readingValue)
                        ) : (
                          <div className="mrc-meter-claimed-wrap">
                            <TextField
                              fullWidth
                              size="small"
                              value={readingValue}
                              onChange={(e) => handleReadingChange(row, e.target.value)}
                              onBlur={() => handleReadingChange(row, readingValue)}
                              placeholder="Enter reading"
                              className="textInputField mrc-meter-input mrc-meter-input--editable"
                              error={Boolean(readingError)}
                              required
                              inputProps={{
                                inputMode: "decimal",
                                "aria-label": `Removed meter reading set ${row.setNo} ${row.meterDialType}`,
                              }}
                            />
                            <div
                              className={`mrc-meter-error-slot${readingError ? " mrc-meter-error-slot--active" : ""}`}
                            >
                              {readingError || ""}
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="cell-text cell-num cell-calc">
                        {isMdi || unitsConsumed == null ? "—" : formatDisplayValue(unitsConsumed)}
                      </td>
                      <td className="cell-site-image">
                        {isMdi ? (
                          <span className="mrc-image-na">—</span>
                        ) : (
                          <div className="mrc-meter-image-wrap">
                            {evidenceSrc ? (
                              <div
                                className="mrc-meter-image-preview mrc-meter-image-preview--clickable"
                                role="button"
                                tabIndex={0}
                                title="Click to view"
                                onClick={() => setPreview({
                                  src: evidenceSrc,
                                  name: `${row.meterDialType} — Set ${row.setNo} Evidence`,
                                })}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    setPreview({
                                      src: evidenceSrc,
                                      name: `${row.meterDialType} — Set ${row.setNo} Evidence`,
                                    });
                                  }
                                }}
                              >
                                <MrcEvidencePreview
                                  src={evidenceSrc}
                                  alt={`${row.meterDialType} evidence set ${row.setNo}`}
                                />
                                {!readOnly ? (
                                  <button
                                    type="button"
                                    className="mrc-meter-image-preview__clear"
                                    aria-label="Remove evidence"
                                    title="Remove to reupload"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      clearEvidence(row.id);
                                    }}
                                  >
                                    <CloseIcon fontSize="inherit" />
                                  </button>
                                ) : null}
                              </div>
                            ) : readOnly ? (
                              <span className="mrc-image-na">—</span>
                            ) : (
                              <Button
                                variant="outlined"
                                component="label"
                                size="small"
                                startIcon={<FileUploadOutlinedIcon fontSize="small" />}
                                className={`mrc-meter-image-btn${evidenceError ? " mrc-meter-image-btn--error" : ""}`}
                                title={evidenceError || "Upload meter face photo"}
                              >
                                Upload
                                <input
                                  hidden
                                  accept={documentExtensions}
                                  type="file"
                                  onChange={(e) => handleEvidenceUpload(row, e)}
                                />
                              </Button>
                            )}
                            {!readOnly ? (
                              <div
                                className={`mrc-meter-error-slot${evidenceError ? " mrc-meter-error-slot--active" : ""}`}
                              >
                                {evidenceError || ""}
                              </div>
                            ) : null}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      {preview?.src ? (
        <OpenBase64ImageInPopup
          docVerifyPopUp={Boolean(preview?.src)}
          hideDocVerifyPopup={() => setPreview(null)}
          documentName={preview.name || "Evidence"}
          documentImage={preview.src}
          width="100%"
          document={preview.src}
          readOnly
          stableViewer
        />
      ) : null}
    </div>
  );
});

DcoRemovedMeterReadingTable.displayName = "DcoRemovedMeterReadingTable";

export default DcoRemovedMeterReadingTable;
